import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';

interface UseLabelsControlOptions {
  map: maplibregl.Map | null;
  showLabels: boolean;
  is3D: boolean;
  onToggle: () => void;
}

export const useLabelsControl = ({
  map,
  showLabels,
  is3D,
  onToggle
}: UseLabelsControlOptions) => {
  const controlRef = useRef<any>(null);

  useEffect(() => {
    if (!map || !is3D) {
      // Remove control if not in 3D mode
      if (controlRef.current) {
        try {
          map?.removeControl(controlRef.current);
        } catch (e) {
          // Control might already be removed
        }
        controlRef.current = null;
      }
      return;
    }

    // Create custom labels toggle control
    class LabelsControl {
      container!: HTMLDivElement;
      button!: HTMLButtonElement;

      onAdd(_map: maplibregl.Map) {
        this.container = document.createElement('div');
        this.container.className = 'maplibregl-ctrl maplibregl-ctrl-group';

        this.button = document.createElement('button');
        this.button.className = 'maplibregl-ctrl-icon';
        this.button.type = 'button';
        this.button.title = showLabels ? 'Hide labels' : 'Show labels';
        this.button.innerHTML = '🏷️';
        this.button.style.fontSize = '16px';

        this.button.addEventListener('click', () => {
          onToggle();
        });

        this.container.appendChild(this.button);
        return this.container;
      }

      onRemove() {
        this.container.parentNode?.removeChild(this.container);
      }
    }

    // Remove existing control if it exists
    if (controlRef.current) {
      try {
        map.removeControl(controlRef.current);
      } catch (e) {
        // Control might already be removed
      }
    }

    // Add new control
    const control = new LabelsControl();
    controlRef.current = control;
    map.addControl(control as any, 'top-right');

    return () => {
      if (map && controlRef.current) {
        try {
          map.removeControl(controlRef.current);
        } catch (e) {
          // Map might be destroyed
        }
      }
    };
  }, [map, showLabels, is3D, onToggle]);
};